/* TypewriterDemo
 *
 * Hero centerpiece — looks like a real ChatGPT/Claude window. Cycles through 4
 * prompts, each routed to a different AI provider:
 *   1) GPT-4o      — cash-flow analysis
 *   2) Claude 3.5  — follow-up email
 *   3) Gemini      — bank reconciliation
 *   4) Perplexity  — market research (web-grounded)
 *
 * The typing animation is client-side (not hitting a real LLM) for zero-cost,
 * deterministic conversions. The visual fidelity (cursor blink, char-by-char
 * reveal, model badge switching, slight pacing variations) sells it as live.
 */
import { useEffect, useState, useRef } from 'react';
import { Sparkles, Zap, Globe, Mail, ScanLine, Search } from 'lucide-react';
import { cn } from '../../lib/utils';

const PROMPTS = [
  {
    model: 'GPT-4o',
    badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    badgeIcon: Sparkles,
    icon: ScanLine,
    prompt: 'Analyze my Q4 cash flow and flag any concerning trends.',
    response: `Looking at your Q4 transactions:

  • Total inflow: $284,320 (+12% QoQ)
  • Total outflow: $241,860 (+18% QoQ)
  • Net cash: +$42,460

⚠ Heads up: vendor payments to "Helix Office Supply" jumped 340% in November — looks like a possible duplicate AP entry. Want me to flag those for review?`,
  },
  {
    model: 'Claude 3.5 Sonnet',
    badge: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
    badgeIcon: Mail,
    icon: Mail,
    prompt: "Draft a friendly follow-up email for invoice #INV-2087 — 14 days overdue.",
    response: `Subject: Quick check-in on Invoice #INV-2087

Hi Marcus,

Hope you're doing well! Just a gentle nudge that Invoice #INV-2087 ($4,200) is now about 2 weeks past due. I know things get busy, so no worries — just wanted to make sure it didn't get lost.

Quick pay link below if helpful:
→ countbucks.app/pay/INV-2087

Best,
Eli`,
  },
  {
    model: 'Gemini 1.5 Pro',
    badge: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
    badgeIcon: Zap,
    icon: ScanLine,
    prompt: 'Reconcile last month\'s bank statements against my QuickBooks ledger.',
    response: `Matched 247 of 251 transactions automatically.

  4 exceptions need your eyes:
  • 11/04  $1,420.00  ACH — no ledger match (Walmart Biz)
  • 11/11  -$89.50    duplicate? appears twice
  • 11/18  $3,200.00  uncategorized deposit
  • 11/27  -$45.00    foreign tx fee, missing in books

Reconciliation drift: $4,575.50. Want me to auto-create draft journal entries?`,
  },
  {
    model: 'Perplexity Sonar',
    badge: 'bg-violet-500/15 text-violet-300 border-violet-500/40',
    badgeIcon: Globe,
    icon: Search,
    prompt: 'Research the average pricing of small-business bookkeeping in Charlotte, NC.',
    response: `Web search (live, sourced):

  • Median monthly retainer: $450–$900/mo¹
  • Hourly rate range: $75–$175/hr²
  • Top-rated boutique firms charge $1,500+ for monthly catch-up packages³

Sources:
  ¹ Thumbtack Charlotte pricing report — Mar 2025
  ² Bench salary survey — 2025
  ³ Local SCORE chapter benchmarking

You're priced ~18% below market median. Want a positioning plan?`,
  },
];

const TYPE_SPEED_PROMPT = 22;        // ms per char
const TYPE_SPEED_RESPONSE = 9;
const PAUSE_BETWEEN_PROMPT_AND_RESPONSE = 600;
const PAUSE_AFTER_FULL = 3200;       // hold before next

export default function TypewriterDemo() {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('prompt'); // 'prompt' | 'response' | 'idle'
  const [typed, setTyped] = useState('');
  const [responseTyped, setResponseTyped] = useState('');
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  useEffect(() => {
    clearTimers();
    const current = PROMPTS[idx];
    setTyped('');
    setResponseTyped('');
    setPhase('prompt');

    // Type the prompt
    for (let i = 0; i <= current.prompt.length; i++) {
      timersRef.current.push(
        setTimeout(() => setTyped(current.prompt.slice(0, i)), i * TYPE_SPEED_PROMPT),
      );
    }

    const promptDoneAt = current.prompt.length * TYPE_SPEED_PROMPT + PAUSE_BETWEEN_PROMPT_AND_RESPONSE;

    // Transition to response phase
    timersRef.current.push(setTimeout(() => setPhase('response'), promptDoneAt));

    // Type the response
    for (let i = 0; i <= current.response.length; i++) {
      timersRef.current.push(
        setTimeout(
          () => setResponseTyped(current.response.slice(0, i)),
          promptDoneAt + i * TYPE_SPEED_RESPONSE,
        ),
      );
    }

    const totalDuration = promptDoneAt + current.response.length * TYPE_SPEED_RESPONSE + PAUSE_AFTER_FULL;

    // Advance to next prompt
    timersRef.current.push(
      setTimeout(() => setIdx((i) => (i + 1) % PROMPTS.length), totalDuration),
    );

    return clearTimers;
  }, [idx]);

  const current = PROMPTS[idx];
  const BadgeIcon = current.badgeIcon;
  const PromptIcon = current.icon;

  return (
    <div
      data-testid="hero-typewriter-demo"
      className="relative mx-auto w-full max-w-3xl"
    >
      {/* Glow */}
      <div className="pointer-events-none absolute -inset-6 bg-gradient-to-br from-emerald-500/20 via-violet-500/15 to-blue-500/20 blur-3xl opacity-50" />

      {/* Window chrome */}
      <div className="relative rounded-2xl border border-white/10 bg-zinc-950/90 backdrop-blur-2xl overflow-hidden shadow-[0_24px_120px_-32px_rgba(16,185,129,0.25)]">
        {/* Title bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-black/40">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
          </div>
          <div className="text-[11px] font-mono text-zinc-500 tracking-widest uppercase">
            countbucks · ai hub
          </div>
          <div className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-mono font-semibold transition-colors duration-500',
            current.badge,
          )} data-testid="hero-model-badge">
            <BadgeIcon className="w-3 h-3" />
            {current.model}
          </div>
        </div>

        {/* Chat body */}
        <div className="px-6 py-6 space-y-4 min-h-[340px]">
          {/* User prompt */}
          <div className="flex gap-3" data-testid="hero-user-prompt">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 border border-white/10 flex items-center justify-center flex-shrink-0">
              <PromptIcon className="w-3.5 h-3.5 text-zinc-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">You</p>
              <p className="text-zinc-200 text-base leading-relaxed">
                {typed}
                {phase === 'prompt' && <span className="inline-block w-[2px] h-4 ml-0.5 bg-emerald-400 animate-pulse" />}
              </p>
            </div>
          </div>

          {/* AI response */}
          {phase !== 'prompt' && (
            <div className="flex gap-3" data-testid="hero-ai-response">
              <div className={cn(
                'w-7 h-7 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors duration-500',
                current.badge,
              )}>
                <Sparkles className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                  {current.model}
                </p>
                <pre className="text-zinc-300 text-sm leading-relaxed font-sans whitespace-pre-wrap break-words">
                  {responseTyped}
                  {phase === 'response' && responseTyped.length < current.response.length && (
                    <span className="inline-block w-[2px] h-4 ml-0.5 bg-violet-400 animate-pulse" />
                  )}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Bottom input bar (decorative) */}
        <div className="px-4 py-3 border-t border-white/5 bg-black/60 flex items-center gap-3">
          <div className="flex-1 px-3 py-2 rounded-lg bg-zinc-900/60 border border-white/5 text-sm text-zinc-600 font-mono">
            Ask anything — Claude · GPT-4o · Gemini · Perplexity · Llama · 240+ more
          </div>
          <button
            type="button"
            disabled
            className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-wider"
          >
            Send
          </button>
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-1.5 mt-5">
        {PROMPTS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            data-testid={`hero-demo-dot-${i}`}
            aria-label={`Show ${PROMPTS[i].model} demo`}
            className={cn(
              'h-1 rounded-full transition-all',
              i === idx ? 'w-8 bg-emerald-400' : 'w-1.5 bg-zinc-700 hover:bg-zinc-500',
            )}
          />
        ))}
      </div>
    </div>
  );
}
